import { useNavigate } from "react-router-dom";
import { liveMetrics } from "../../../mocks/platformData";

const metrics = [
  { label: "처리된 에피소드", value: liveMetrics.episodesProcessed.toLocaleString(), icon: "ri-film-line", color: "#00D9FF" },
  { label: "장면 분석 정확도", value: `${liveMetrics.sceneAnalysisAccuracy}%`, icon: "ri-brain-line", color: "#10B981" },
  { label: "숏폼 전환율", value: `${liveMetrics.shortFormConversionRate}%`, icon: "ri-bar-chart-line", color: "#F59E0B" },
  { label: "평균 제작 시간", value: `${liveMetrics.avgProductionMinutes}분`, icon: "ri-timer-line", color: "#00D9FF" },
];

export default function HeroSection() {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#0A0E14]">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/869e186f17f05e4e7b51d4df483c41c5.jpg"
          alt="StreamClip AI 케이블TV 플랫폼 배경"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E14]/70 via-[#0A0E14]/60 to-[#0A0E14]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E14]/80 via-transparent to-[#0A0E14]/60" />
      </div>

      {/* Floating grid pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: "linear-gradient(rgba(0,217,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,217,255,0.3) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-32 pb-16">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <span
            className="inline-flex items-center gap-2 bg-[#00D9FF]/10 border border-[#00D9FF]/30 text-[#00D9FF] text-xs font-semibold px-4 py-2 rounded-full uppercase tracking-widest"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <span className="w-2 h-2 flex items-center justify-center">
              <i className="ri-tv-2-line text-xs" />
            </span>
            케이블TV · 방송 콘텐츠 AI 숏폼 자동화 엔진
          </span>
        </div>

        {/* Headline */}
        <div className="text-center max-w-5xl mx-auto">
          <h1
            className="text-6xl md:text-7xl font-bold text-white leading-[1.08] tracking-tight mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            방송 프로그램의 핵심 순간을
            <br />
            <span className="text-[#00D9FF]">즉시 바이럴 숏폼</span>
            <span className="italic text-gray-300">으로</span>
          </h1>
          <p
            className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed mb-10"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            케이블TV·종편·지상파 방송 에피소드를 업로드하면,
            <br />
            AI가 핵심 장면을 자동 감지하고 숏폼으로 제작·배포까지
            <br />
            전 과정을 단 몇 분 안에 완성합니다.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <button
              onClick={() => navigate("/onboarding")}
              className="bg-[#00D9FF] hover:bg-[#00D9FF]/90 text-[#0A0E14] font-bold text-base px-8 py-4 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap w-full sm:w-auto"
            >
              무료로 시작하기
            </button>
            <button className="flex items-center gap-2 border border-white/20 hover:border-[#00D9FF]/50 text-white text-base font-medium px-8 py-4 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap w-full sm:w-auto justify-center">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-play-circle-line text-[#00D9FF]" />
              </span>
              데모 확인하기
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            {["신용카드 불필요", "14일 무료 체험", "언제든지 취소 가능"].map((item) => (
              <span key={item} className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-[#10B981] text-sm" />
                </span>
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* Metrics Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 max-w-4xl mx-auto">
          {metrics.map((m) => (
            <div
              key={m.label}
              className="bg-[#0F1419]/80 backdrop-blur-sm border border-white/8 rounded-xl p-5 text-center"
            >
              <div
                className="w-10 h-10 flex items-center justify-center rounded-lg mx-auto mb-3"
                style={{ backgroundColor: `${m.color}15` }}
              >
                <i className={`${m.icon} text-xl`} style={{ color: m.color }} />
              </div>
              <div
                className="text-2xl font-bold text-white mb-1"
                style={{ fontFamily: "'JetBrains Mono', monospace", color: m.color }}
              >
                {m.value}
              </div>
              <div className="text-xs text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>
                {m.label}
              </div>
            </div>
          ))}
        </div>

        {/* Trusted by */}
        <div className="mt-14 text-center">
          <p className="text-xs uppercase tracking-widest text-gray-600 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            국내 주요 방송사 및 제작사가 신뢰하는 플랫폼
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-50">
            {["tvN", "JTBC", "MBC", "KBS Media", "OCN"].map((brand) => (
              <span
                key={brand}
                className="text-gray-400 font-semibold text-sm tracking-wide"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
